import supabase from "./supabase";
import { compressImage } from "./imageCompress";

export interface ServiceRow {
  id?: string; name: string; slug: string; icon?: string | null;
  short_description?: string | null;
  description_1?: string | null; description_2?: string | null;
  carousel_images?: string[]; featured_image_url?: string | null;
  hero_image_url?: string | null;
  hero_quote?: string | null;
  brands?: { name: string; logo_url?: string; width?: number; height?: number; font_size?: number }[];
  sort_order?: number; published?: boolean;
}

export interface GalleryProjectRow {
  id?: string; title: string; slug: string; category?: string | null;
  created_at?: string;
  hero_image_url?: string | null;
  description_1?: string | null; description_2?: string | null;
  photos?: string[]; youtube_url?: string | null;
  published?: boolean; sort_order?: number;
  seo_title?: string | null; seo_description?: string | null; seo_og_image?: string | null;
}

export interface CaseStudyStat { label: string; value: string }
export interface CaseStudyRow {
  id?: string; title: string; slug: string;
  client?: string | null; category?: string | null;
  event_date?: string | null; venue?: string | null; location?: string | null; audience_size?: string | null;
  hero_image_url?: string | null;
  challenge?: string | null; solution?: string | null; execution?: string | null; result?: string | null;
  technical_specs?: string | null; team_credits?: string | null;
  photos?: string[]; youtube_url?: string | null;
  testimonial_quote?: string | null; testimonial_author?: string | null;
  stats?: CaseStudyStat[];
  published?: boolean; sort_order?: number;
  seo_title?: string | null; seo_description?: string | null; seo_og_image?: string | null;
  created_at?: string;
}

export interface JobListingRow {
  id?: string; title: string; slug?: string | null;
  employment_type?: string | null;
  location?: string | null; description?: string | null;
  tags?: string[]; published?: boolean;
}

export interface SettingsMap { [key: string]: string }

// ─── Services ─────────────────────────────────────────────
export const listServices = async (publishedOnly = false): Promise<ServiceRow[]> => {
  let q = supabase().from("services").select("*").order("sort_order", { ascending: true });
  if (publishedOnly) q = q.eq("published", true);
  const { data, error } = await q;
  if (error) throw error;
  return (data as ServiceRow[]) || [];
};
export const getServiceBySlug = async (slug: string): Promise<ServiceRow | null> => {
  const { data, error } = await supabase().from("services").select("*").eq("slug", slug).eq("published", true).maybeSingle();
  if (error) throw error;
  return data as ServiceRow | null;
};
export const upsertService = async (row: ServiceRow): Promise<ServiceRow> => {
  const { data, error } = await supabase().from("services").upsert(row, { onConflict: "slug" }).select().single();
  if (error) throw error;
  return data as ServiceRow;
};
export const deleteService = async (id: string) => {
  const { error } = await supabase().from("services").delete().eq("id", id);
  if (error) throw error;
};

// ─── Gallery ───────────────────────────────────────────────
export const listProjects = async (publishedOnly = false): Promise<GalleryProjectRow[]> => {
  // Manual order wins where it has been set; anything without an explicit
  // sort_order falls back to newest-first, so fresh projects surface at the top
  // until an admin drags them into place.
  let q = supabase()
    .from("gallery_projects")
    .select("*")
    .order("sort_order", { ascending: true, nullsFirst: true })
    .order("created_at", { ascending: false });
  if (publishedOnly) q = q.eq("published", true);
  const { data, error } = await q;
  if (error) throw error;
  return (data as GalleryProjectRow[]) || [];
};
export const getProjectBySlug = async (slug: string): Promise<GalleryProjectRow | null> => {
  const { data, error } = await supabase().from("gallery_projects").select("*").eq("slug", slug).eq("published", true).maybeSingle();
  if (error) throw error;
  return data as GalleryProjectRow | null;
};
export const upsertProject = async (row: GalleryProjectRow): Promise<GalleryProjectRow> => {
  const { data, error } = await supabase().from("gallery_projects").upsert(row, { onConflict: "slug" }).select().single();
  if (error) throw error;
  return data as GalleryProjectRow;
};
/** Persist a new manual order. Writes sort_order = array index for every row. */
export const reorderProjects = async (orderedIds: string[]): Promise<void> => {
  const results = await Promise.all(
    orderedIds.map((id, i) =>
      supabase().from("gallery_projects").update({ sort_order: i }).eq("id", id)
    )
  );
  const failed = results.find(r => r.error);
  if (failed?.error) throw failed.error;
};

export const deleteProject = async (id: string) => {
  const { error } = await supabase().from("gallery_projects").delete().eq("id", id);
  if (error) throw error;
};

// ─── Case Studies ──────────────────────────────────────────
export const listCaseStudies = async (publishedOnly = false): Promise<CaseStudyRow[]> => {
  // Same ordering rule as gallery: explicit sort_order first (nulls at top),
  // then newest first so unordered rows surface until the admin places them.
  let q = supabase()
    .from("case_studies")
    .select("*")
    .order("sort_order", { ascending: true, nullsFirst: true })
    .order("created_at", { ascending: false });
  if (publishedOnly) q = q.eq("published", true);
  const { data, error } = await q;
  if (error) throw error;
  return (data as CaseStudyRow[]) || [];
};
export const getCaseStudyBySlug = async (slug: string): Promise<CaseStudyRow | null> => {
  const { data, error } = await supabase().from("case_studies").select("*").eq("slug", slug).eq("published", true).maybeSingle();
  if (error) throw error;
  return data as CaseStudyRow | null;
};
export const upsertCaseStudy = async (row: CaseStudyRow): Promise<CaseStudyRow> => {
  const { data, error } = await supabase().from("case_studies").upsert(row, { onConflict: "slug" }).select().single();
  if (error) throw error;
  return data as CaseStudyRow;
};
export const reorderCaseStudies = async (orderedIds: string[]): Promise<void> => {
  const results = await Promise.all(
    orderedIds.map((id, i) =>
      supabase().from("case_studies").update({ sort_order: i }).eq("id", id)
    )
  );
  const failed = results.find(r => r.error);
  if (failed?.error) throw failed.error;
};
export const deleteCaseStudy = async (id: string) => {
  const { error } = await supabase().from("case_studies").delete().eq("id", id);
  if (error) throw error;
};

// ─── Jobs ──────────────────────────────────────────────────
export const listJobs = async (publishedOnly = false): Promise<JobListingRow[]> => {
  let q = supabase().from("job_listings").select("*").order("created_at", { ascending: false });
  if (publishedOnly) q = q.eq("published", true);
  const { data, error } = await q;
  if (error) throw error;
  return (data as JobListingRow[]) || [];
};
export const upsertJob = async (row: JobListingRow): Promise<JobListingRow> => {
  const { data, error } = await supabase().from("job_listings").upsert(row).select().single();
  if (error) throw error;
  return data as JobListingRow;
};
export const deleteJob = async (id: string) => {
  const { error } = await supabase().from("job_listings").delete().eq("id", id);
  if (error) throw error;
};

// ─── Submissions ───────────────────────────────────────────
export const listSubmissions = async (table: string) => {
  const { data, error } = await supabase().from(table).select("*").order("submitted_at", { ascending: false });
  if (error) throw error;
  return data as any[] || [];
};
export const updateSubmission = async (table: string, id: string, patch: Record<string, any>) => {
  const { error } = await supabase().from(table).update(patch).eq("id", id);
  if (error) throw error;
};
export const deleteSubmission = async (table: string, id: string) => {
  const { error } = await supabase().from(table).delete().eq("id", id);
  if (error) throw error;
};

// ─── Site Settings (key/value) ─────────────────────────────
export const loadSettings = async (): Promise<SettingsMap> => {
  const { data, error } = await supabase().from("site_settings").select("key, value");
  if (error) throw error;
  const map: SettingsMap = {};
  for (const r of data || []) map[r.key] = r.value || "";
  return map;
};
export const setSetting = async (key: string, value: string) => {
  const { error } = await supabase().from("site_settings").upsert({ key, value, updated_at: new Date().toISOString() }, { onConflict: "key" });
  if (error) throw error;
};
export const setSettings = async (entries: Record<string, string>) => {
  const rows = Object.entries(entries).map(([key, value]) => ({ key, value, updated_at: new Date().toISOString() }));
  const { error } = await supabase().from("site_settings").upsert(rows, { onConflict: "key" });
  if (error) throw error;
};

// ─── Storage uploads (admin) ───────────────────────────────
export const uploadAdminFile = async (folder: string, file: File): Promise<string> => {
  // Resize + convert to WebP in the browser first. Returns the original file
  // untouched for SVG/GIF/non-images, or if compression wouldn't be a win.
  const prepared = await compressImage(file);
  const safe = prepared.name.replace(/[^A-Za-z0-9._-]/g, "_");
  const path = `${folder}/${Date.now()}_${Math.random().toString(36).slice(2, 8)}_${safe}`;
  const { error } = await supabase().storage.from("images").upload(path, prepared, {
    cacheControl: "3600",
    upsert: false,
    contentType: prepared.type || undefined,
  });
  if (error) throw error;
  const { data } = supabase().storage.from("images").getPublicUrl(path);
  return data.publicUrl;
};

// ─── Dashboard counters ─────────────────────────────────────
export const dashboardCounts = async () => {
  const sb = supabase();
  const [quotes, contact, applic, freel, gallery, cases, jobs] = await Promise.all([
    sb.from("quote_requests").select("id, read, submitted_at", { count: "exact" }),
    sb.from("contact_messages").select("id, read, submitted_at", { count: "exact" }),
    sb.from("job_applications").select("id, read, submitted_at", { count: "exact" }),
    sb.from("freelancer_submissions").select("id, reviewed, submitted_at", { count: "exact" }),
    sb.from("gallery_projects").select("id, published", { count: "exact" }).eq("published", true),
    sb.from("case_studies").select("id, published", { count: "exact" }).eq("published", true),
    sb.from("job_listings").select("id, published", { count: "exact" }).eq("published", true),
  ]);
  const dayAgo = Date.now() - 86400_000; const weekAgo = Date.now() - 7 * 86400_000;
  const unread = (rows: any[] | null, flag = "read") => (rows || []).filter(r => !r[flag]).length;
  const tally = (rows: any[] | null, since: number) => (rows || []).filter(r => new Date(r.submitted_at).getTime() > since).length;
  return {
    quotes: { total: quotes.data?.length || 0, unread: unread(quotes.data), today: tally(quotes.data, dayAgo), week: tally(quotes.data, weekAgo) },
    contact: { total: contact.data?.length || 0, unread: unread(contact.data) },
    applications: { total: applic.data?.length || 0, unread: unread(applic.data) },
    freelancers: { total: freel.data?.length || 0, unread: unread(freel.data, "reviewed") },
    gallery_published: gallery.count || 0,
    case_studies_published: cases.count || 0,
    jobs_published: jobs.count || 0,
  };
};

export const recentActivity = async (limit = 10) => {
  const sb = supabase();
  const tables = [
    { t: "quote_requests", label: "Quote Request" },
    { t: "contact_messages", label: "Contact Message" },
    { t: "job_applications", label: "Job Application" },
    { t: "freelancer_submissions", label: "Freelancer" },
  ];
  const all = await Promise.all(tables.map(async ({ t, label }) => {
    const { data } = await sb.from(t).select("id, first_name, last_name, email, submitted_at").order("submitted_at", { ascending: false }).limit(limit);
    return (data || []).map(r => ({ ...r, _type: label, _table: t }));
  }));
  const flat = all.flat().sort((a, b) => new Date(b.submitted_at).getTime() - new Date(a.submitted_at).getTime()).slice(0, limit);
  return flat;
};
